import type { Control } from "react-hook-form";
import { Controller, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { UploadForm } from "~/utils/upload-form";
import { Card, Collapse, Switch, Text } from "~/components/ui";

type UploadOptionsCardProps = {
  control: Control<UploadForm>;
};

export function UploadOptionsCard({ control }: UploadOptionsCardProps) {
  const { t } = useTranslation("admin");
  const deleteCollection = useWatch({ control, name: "deleteCollection" });

  const advancedItems = [
    {
      key: "advanced",
      label: t("uploadAdmin.advanced.title"),
      children: (
        <div className="flex items-center gap-2">
          <Controller
            name="skipIngestion"
            control={control}
            render={({ field }) => (
              <Switch
                size="small"
                checked={field.value}
                onChange={field.onChange}
                aria-label={t("uploadAdmin.advanced.skipIngestion")}
              />
            )}
          />
          <div>
            <span className="text-noa">{t("uploadAdmin.advanced.skipIngestion")}</span>
            <span className="text-xs text-noa-muted block">{t("uploadAdmin.advanced.skipIngestionDescription")}</span>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <Card title={t("uploadAdmin.upload.options")} size="small">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Overwrite toggle */}
          <div className="p-3 bg-noa-hover rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <Text>{t("uploadAdmin.form.overwrite")}</Text>
                <div className="text-xs text-noa-muted mt-1">{t("uploadAdmin.form.overwriteDescription")}</div>
              </div>
              <Controller
                name="overwrite"
                control={control}
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onChange={field.onChange}
                    aria-label={t("uploadAdmin.form.overwrite")}
                  />
                )}
              />
            </div>
          </div>

          {/* Delete collection toggle */}
          <div className={`p-3 rounded-lg ${deleteCollection ? "bg-red-500/10" : "bg-noa-hover"}`}>
            <div className="flex items-center justify-between">
              <div>
                <Text className={deleteCollection ? "text-noa-danger" : ""}>
                  {t("uploadAdmin.form.deleteCollection")}
                </Text>
                {deleteCollection && (
                  <div className="text-xs text-noa-danger mt-1">
                    ⚠️ {t("uploadAdmin.form.deleteCollectionDescription")}
                  </div>
                )}
              </div>
              <Controller
                name="deleteCollection"
                control={control}
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onChange={field.onChange}
                    aria-label={t("uploadAdmin.form.deleteCollection")}
                  />
                )}
              />
            </div>
          </div>
        </div>
      </Card>

      <Collapse items={advancedItems} size="small" />
    </>
  );
}
